import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { CepModel } from './models/cep.model';
import { Response } from './models/response';
import { BankModel } from './models/hiring/bank.model';
import { StateModel } from './models/state.model';
import { HttpService } from './http.service';
import { CountryModel } from './models/country.model';
import { DistrictModel } from './models/district.model';

@Injectable()
export class GeographyService {
  private readonly cepBaseUrl = environment.cepService;
  private readonly brasilUrl = 'https://brasilapi.com.br/api/';
  public readonly defaultCountry = 'Brasil';

  constructor(
    private _httpClient: HttpClient,
    private _httpService: HttpService
  ) { }

  public getCountries(disabledLoading: boolean = false): Observable<Response<CountryModel[]>> {
    return this._httpService
      .get(`geography/countries/odata?$orderBy=name`, null, null, null, disabledLoading)
      .pipe(
        map((res: any) => {
          const response = new Response<CountryModel[]>();
          response.data = res.body.map((e: CountryModel) => CountryModel.create(e));

          return response;
        })
      );
  }

  public getStates(disabledLoading: boolean = false): Observable<Response<StateModel[]>> {
    return this._httpService
      .get(`geography/states/odata?$orderBy=name`, null, null, null, disabledLoading)
      .pipe(
        map((res: any) => {
          const response = new Response<StateModel[]>();
          response.data = res.body.map((e: StateModel) => StateModel.create(e));

          return response;
        })
      );
  }

  public getDistricts(disabledLoading: boolean = false): Observable<Response<DistrictModel[]>> {
    return this._httpService
      .get(`geography/districts/odata?$orderBy=name`, null, null, null, disabledLoading)
      .pipe(
        map((res: any) => {
          const response = new Response<DistrictModel[]>();
          response.data = res.body.map((e: DistrictModel) => DistrictModel.create(e));

          return response;
        })
      );
  }

  public getDistrictsByName(search: string): Observable<Response<DistrictModel[]>> {
    const term = search || '';
    const filter = term ? `$filter=contains(toupper(name),'${term}')` : '';

    return this._httpService
      .get(`geography/districts/odata?${filter}&$orderBy=name`, null, null, null, true)
      .pipe(
        map((res: any) => {
          const response = new Response<DistrictModel[]>();
          response.data = res.body.map((e: DistrictModel) => DistrictModel.create(e));
          return response;
        })
      );
  }

  public getDistrictsByState(
    state: string,
    disabledLoading: boolean = false
  ): Observable<Response<DistrictModel[]>> {
    return this._httpService
      .get(
        `geography/districts/odata?$filter=state eq '${state}'&$orderBy=name`,
        null,
        null,
        null,
        true
      )
      .pipe(
        map((res: any) => {
          const response = new Response<DistrictModel[]>();
          response.data = res.body.map((e: DistrictModel) => DistrictModel.create(e));

          return response;
        })
      );
  }

  public search(cep: string): Observable<Response<CepModel>> {
    return new Observable<Response<CepModel>>(observer => {
      const http = new XMLHttpRequest();
      http.open('get', this.getEndpoint(cep), true);
      http.send();

      http.onload = function () {
        const response = new Response<CepModel>();
        response.data = CepModel.create(JSON.parse(this.response));

        observer.next(response);
      };
    });
  }

  private getEndpoint(cep: string): string {
    return this.cepBaseUrl.replace('{cep}', cep);
  }

  public getBanks(): Observable<Response<BankModel[]>> {
    return this._httpClient.get(`${this.brasilUrl}banks/v1`).pipe(
      map((res: any) => {
        const response = new Response<BankModel[]>();
        response.data = res
          .map((e: BankModel) => BankModel.create(e))
          .filter((e: { code: BankModel }) => e.code)
          .sort((a: { name: any }, b: { name: any }) =>
            a.name > b.name ? 1 : a.name < b.name ? -1 : 0
          );
        return response;
      })
    );
  }
}
